import { useMemo, useState } from "react";
import { useGetIdentity, useGetList, useTranslate } from "ra-core";
import { Link } from "react-router";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { getStageAccent, formatMoney, stageHasPrice } from "@/lib/arkaTokens";
import {
  buildDashboardMetrics,
  formatAmountsByCurrency,
  getDealCurrency,
  type DashboardPeriod,
} from "@/lib/dashboardMetrics";
import { findOriginLabel } from "@/lib/originChannels";
import { getProductLabel } from "@/lib/arkaCatalog";
import type { Deal, Task } from "../types";
import { findDealLabel } from "../deals/dealUtils";
import { useConfigurationContext } from "../root/ConfigurationContext";
import { TasksList } from "./TasksList";

export const Dashboard = () => {
  const translate = useTranslate();
  const { identity, isPending: isIdentityPending } = useGetIdentity();
  const namedIdentity = identity as
    | { displayName?: string; fullName?: string }
    | undefined;
  const callName =
    namedIdentity?.displayName?.trim() ||
    namedIdentity?.fullName?.split(" ")[0] ||
    "equipo";
  const { dealStages, dealCategories } = useConfigurationContext();
  const [period, setPeriod] = useState<DashboardPeriod>("week");
  const [stageFilter, setStageFilter] = useState("all");
  const [familyFilter, setFamilyFilter] = useState("all");

  const { data: deals = [], isPending } = useGetList<Deal>(
    "deals",
    {
      pagination: { page: 1, perPage: 500 },
      sort: { field: "updated_at", order: "DESC" },
      filter: { "archived_at@is": null },
    },
    { enabled: Number.isInteger(identity?.id) },
  );
  const { data: tasks = [] } = useGetList<Task>(
    "tasks",
    {
      pagination: { page: 1, perPage: 500 },
      sort: { field: "due_date", order: "ASC" },
      filter: { "done_date@is": null },
    },
    { enabled: Number.isInteger(identity?.id) },
  );

  const filteredDeals = useMemo(() => {
    return deals.filter((deal) => {
      if (stageFilter !== "all" && deal.stage !== stageFilter) {
        return false;
      }
      if (familyFilter !== "all" && deal.category !== familyFilter) {
        return false;
      }
      return true;
    });
  }, [deals, familyFilter, stageFilter]);

  const metrics = useMemo(
    () => buildDashboardMetrics(filteredDeals, tasks, period),
    [filteredDeals, period, tasks],
  );

  if (isIdentityPending || (Number.isInteger(identity?.id) && isPending)) {
    return <p className="text-sm text-foreground">Cargando tu tablero…</p>;
  }

  return (
    <div className="flex flex-col gap-6">
      <div>
        <p className="text-sm text-muted-foreground">
          {translate("crm.dashboard.greeting", { _: "Buenas tardes" })},{" "}
          {callName}
        </p>
        <h1 className="text-2xl font-semibold tracking-tight">
          {translate("crm.dashboard.title", { _: "Métricas" })}
        </h1>
        <p className="mt-1 text-sm text-muted-foreground">
          {translate("crm.dashboard.pipeline_summary", {
            open: metrics.openCount,
            _: `${metrics.openCount} negocios en funnel · ${metrics.onboardingCount} en producción · ${metrics.activeCount} clientes activos.`,
          })}
        </p>
      </div>

      <div className="flex flex-wrap gap-2">
        <PeriodButton
          current={period}
          value="week"
          onSelect={setPeriod}
          label="Esta semana"
        />
        <PeriodButton
          current={period}
          value="month"
          onSelect={setPeriod}
          label="Este mes"
        />
        <PeriodButton
          current={period}
          value="all"
          onSelect={setPeriod}
          label="Todo"
        />
        <select
          className="h-9 rounded-md border border-input bg-background px-3 text-sm"
          value={stageFilter}
          onChange={(event) => setStageFilter(event.target.value)}
          aria-label={translate("resources.deals.fields.stage")}
        >
          <option value="all">Todas las etapas</option>
          {dealStages.map((stage) => (
            <option key={stage.value} value={stage.value}>
              {stage.label}
            </option>
          ))}
        </select>
        <select
          className="h-9 rounded-md border border-input bg-background px-3 text-sm"
          value={familyFilter}
          onChange={(event) => setFamilyFilter(event.target.value)}
          aria-label={translate("resources.deals.fields.category")}
        >
          <option value="all">Todas las familias</option>
          {dealCategories.map((family) => (
            <option key={family.value} value={family.value}>
              {family.label}
            </option>
          ))}
        </select>
      </div>

      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        <KpiCard label="En funnel" value={String(metrics.openCount)} />
        <KpiCard
          label="En producción"
          value={String(metrics.onboardingCount)}
        />
        <KpiCard label="Clientes activos" value={String(metrics.activeCount)} />
        <KpiCard
          label="Tasa de cierre"
          value={`${Math.round(metrics.winRate)}%`}
        />
        <KpiCard
          label="Pendientes vencidas"
          value={String(metrics.pendingOverdue)}
        />
        <KpiCard label="Pendientes hoy" value={String(metrics.pendingToday)} />
        <KpiCard
          label="Actividades abiertas"
          value={String(metrics.pendingAll)}
        />
        <KpiCard
          label="Cierres a tiempo"
          value={
            metrics.onTimeRate == null
              ? "—"
              : `${Math.round(metrics.onTimeRate)}%`
          }
        />
        <KpiCard
          label="Velocidad"
          value={
            metrics.velocityDays == null ? "—" : `${metrics.velocityDays} días`
          }
        />
      </div>

      <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
        <CurrencyBlock title="COP" bucket={metrics.byCurrency.COP} />
        <CurrencyBlock title="USD" bucket={metrics.byCurrency.USD} />
      </div>

      <OriginBreakdown deals={filteredDeals} />

      <div className="grid grid-cols-1 gap-4 xl:grid-cols-5">
        <Card className="xl:col-span-3 p-5">
          <h2 className="mb-4 text-sm font-medium">Pipeline por etapa</h2>
          <div className="flex flex-col gap-3">
            {dealStages
              .filter((stage) => stage.value !== "lost")
              .map((stage) => {
                const stageDeals = filteredDeals.filter(
                  (deal) => deal.stage === stage.value,
                );
                const amountByCurrency = stageDeals.reduce<
                  Record<string, number>
                >((acc, deal) => {
                  if (!stageHasPrice(stage.value) || !deal.amount) {
                    return acc;
                  }
                  const currency = getDealCurrency(deal);
                  acc[currency] = (acc[currency] ?? 0) + deal.amount;
                  return acc;
                }, {});
                const max = Math.max(
                  ...dealStages.map(
                    (item) =>
                      filteredDeals.filter((deal) => deal.stage === item.value)
                        .length,
                  ),
                  1,
                );
                const width = `${Math.max((stageDeals.length / max) * 100, 4)}%`;
                const amountLabel = formatAmountsByCurrency(
                  amountByCurrency,
                  formatMoney,
                );
                return (
                  <div key={stage.value} className="flex flex-col gap-1">
                    <div className="flex items-center justify-between text-xs text-muted-foreground">
                      <span>
                        {stage.label} · {stageDeals.length}
                      </span>
                      <span>
                        {stageHasPrice(stage.value)
                          ? amountLabel || "—"
                          : `${stageDeals.length}`}
                      </span>
                    </div>
                    <div className="h-2 overflow-hidden rounded-full bg-muted">
                      <div
                        className="h-full rounded-full"
                        style={{
                          width,
                          backgroundColor: getStageAccent(stage.value),
                        }}
                      />
                    </div>
                  </div>
                );
              })}
          </div>
        </Card>
        <Card className="xl:col-span-2 p-5">
          <h2 className="mb-4 text-sm font-medium">Qué se vendió</h2>
          <ProductBreakdown
            byProduct={metrics.byProduct}
            emptyLabel="Aún no hay cierres en este periodo."
          />
        </Card>
      </div>

      <Card className="overflow-hidden p-0">
        <div className="flex items-center justify-between px-5 py-4">
          <h2 className="text-sm font-medium">Negocios recientes</h2>
          <Link to="/deals" className="text-sm text-primary no-underline">
            Pipeline
          </Link>
        </div>
        <RecentDealsTable deals={filteredDeals.slice(0, 8)} />
      </Card>

      <TasksList />
    </div>
  );
};

interface KpiCardProps {
  label: string;
  value: string;
}

function KpiCard({ label, value }: KpiCardProps) {
  return (
    <Card className="p-4">
      <p className="text-xs uppercase tracking-wide text-muted-foreground">
        {label}
      </p>
      <p className="mt-2 text-2xl font-semibold">{value}</p>
    </Card>
  );
}

function PeriodButton({
  current,
  value,
  onSelect,
  label,
}: {
  current: DashboardPeriod;
  value: DashboardPeriod;
  onSelect: (period: DashboardPeriod) => void;
  label: string;
}) {
  const isActive = current === value;
  return (
    <Button
      type="button"
      size="sm"
      variant={isActive ? "default" : "outline"}
      className="h-9"
      onClick={() => onSelect(value)}
    >
      {label}
    </Button>
  );
}

function CurrencyBlock({
  title,
  bucket,
}: {
  title: string;
  bucket: { pipeline: number; won: number; mrr: number; wonCount: number };
}) {
  return (
    <Card className="p-5">
      <h2 className="mb-4 text-sm font-medium">{title}</h2>
      <div className="grid grid-cols-3 gap-3">
        <div>
          <p className="text-xs text-muted-foreground">Pipeline cotizado</p>
          <p className="mt-1 text-lg font-semibold">
            {formatMoney(bucket.pipeline, title)}
          </p>
        </div>
        <div>
          <p className="text-xs text-muted-foreground">Ganado en el periodo</p>
          <p className="mt-1 text-lg font-semibold">
            {formatMoney(bucket.won, title)}
          </p>
        </div>
        <div>
          <p className="text-xs text-muted-foreground">MRR</p>
          <p className="mt-1 text-lg font-semibold">
            {formatMoney(bucket.mrr, title)}
          </p>
        </div>
      </div>
    </Card>
  );
}

function ProductBreakdown({
  byProduct,
  emptyLabel,
}: {
  byProduct: Record<string, { count: number; wonCop: number; wonUsd: number }>;
  emptyLabel: string;
}) {
  const entries = Object.entries(byProduct).filter(
    ([, item]) => item.wonCop > 0 || item.wonUsd > 0,
  );
  if (entries.length === 0) {
    return <p className="text-sm text-muted-foreground">{emptyLabel}</p>;
  }
  return (
    <div className="flex flex-col gap-3">
      {entries.map(([product, item]) => (
        <div
          key={product}
          className="flex items-start justify-between gap-3 text-sm"
        >
          <span>{getProductLabel(product) || product}</span>
          <span className="text-muted-foreground">
            {item.wonCop > 0 ? formatMoney(item.wonCop, "COP") : ""}
            {item.wonCop > 0 && item.wonUsd > 0 ? " · " : ""}
            {item.wonUsd > 0 ? formatMoney(item.wonUsd, "USD") : ""}
          </span>
        </div>
      ))}
    </div>
  );
}

function RecentDealsTable({ deals }: { deals: Deal[] }) {
  const { dealStages } = useConfigurationContext();
  const translate = useTranslate();

  if (deals.length === 0) {
    return (
      <p className="px-5 pb-5 text-sm text-muted-foreground">
        {translate("crm.dashboard.no_deals", {
          _: "Aún no hay negocios. Crea uno en Pipeline.",
        })}
      </p>
    );
  }

  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead className="border-t border-border text-left text-xs uppercase tracking-wide text-muted-foreground">
          <tr>
            <th className="px-5 py-2 font-medium">
              {translate("resources.deals.fields.name")}
            </th>
            <th className="px-5 py-2 font-medium">Producto</th>
            <th className="px-5 py-2 font-medium">
              {translate("resources.deals.fields.stage")}
            </th>
            <th className="px-5 py-2 font-medium">
              {translate("resources.deals.fields.amount")}
            </th>
          </tr>
        </thead>
        <tbody>
          {deals.map((deal) => (
            <tr key={deal.id} className="border-t border-border">
              <td className="px-5 py-3">
                <Link to={`/deals/${deal.id}/show`} className="no-underline">
                  {deal.name}
                </Link>
              </td>
              <td className="px-5 py-3 text-muted-foreground">
                {getProductLabel(deal.product) || "—"}
              </td>
              <td className="px-5 py-3 text-muted-foreground">
                {findDealLabel(dealStages, deal.stage) ?? deal.stage}
              </td>
              <td className="px-5 py-3">
                {stageHasPrice(deal.stage) && deal.amount > 0
                  ? formatMoney(deal.amount, getDealCurrency(deal))
                  : "—"}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function OriginBreakdown({ deals }: { deals: Deal[] }) {
  const translate = useTranslate();
  const counts = deals.reduce<Record<string, number>>((acc, deal) => {
    const key = deal.origin || "manual";
    acc[key] = (acc[key] ?? 0) + 1;
    return acc;
  }, {});
  const entries = Object.entries(counts);

  if (entries.length === 0) {
    return null;
  }

  return (
    <Card className="p-5">
      <h2 className="mb-4 text-sm font-medium">
        {translate("crm.dashboard.origin_channels", {
          _: "Canales de captación",
        })}
      </h2>
      <div className="flex flex-wrap gap-3">
        {entries.map(([value, count]) => (
          <div
            key={value}
            className="rounded-lg border border-border px-3 py-2 text-sm"
          >
            <p className="text-xs text-muted-foreground">
              {findOriginLabel(value) || value}
            </p>
            <p className="text-lg font-semibold">{count}</p>
          </div>
        ))}
      </div>
    </Card>
  );
}
